import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {
  ApiCollection,
  ApiResponse,
  Attachment,
  PolicyAcceptance,
  PolicyAssignment,
  PolicyAcceptanceSummary,
  PolicyDetail,
  PolicyInfluence,
  PolicyRow
} from '@core/types';
import {CONFIG, ENDPOINTS, GET_URL, log} from '@core/utils';
import {HttpService} from '@core/services';
import {CompanyStateService} from '@core/states';
import {AuthStateService} from '@auth/states';

@Injectable({
  providedIn: 'root'
})
export class PolicyService {
  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async getPolicies(page = 1, itemsPerPage = 50, filters: {
    status?: string;
    search?: string;
  } = {}, options?: {skipLoader?: boolean}): Promise<ApiResponse<PolicyRow[]>> {
    try {
      const url = GET_URL(ENDPOINTS.POLICIES, {page, itemsPerPage, ...filters});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const policies = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapPolicy(item));
      return {success: true, result: policies};
    } catch (error: any) {
      log.error('Error loading policies:', error);
    }
    return {success: false, message: 'No se pudieron cargar las politicas.'};
  }

  async getPolicy(policyId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<PolicyDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.POLICY_DETAIL, {id: policyId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.mapPolicyDetail(response)};
    } catch (error: any) {
      log.error('Error loading policy detail:', error);
    }
    return {success: false, message: 'No se pudo cargar la politica.'};
  }

  async getPolicyAcceptanceSummary(options?: {skipLoader?: boolean}): Promise<ApiResponse<PolicyAcceptanceSummary[]>> {
    try {
      const url = GET_URL(ENDPOINTS.POLICY_ACCEPTANCE_SUMMARY);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<PolicyAcceptanceSummary> = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.apiService.normalizeCollection<PolicyAcceptanceSummary>(response).member};
    } catch (error: any) {
      log.error('Error loading policy acceptance summary:', error);
    }
    return {success: false, message: 'No se pudo cargar el resumen de politicas.'};
  }

  async getPolicyAcceptances(policyId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<PolicyAcceptance[]>> {
    try {
      const url = GET_URL(ENDPOINTS.POLICY_ACCEPTANCES, {id: policyId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const acceptances = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapPolicyAcceptance(item));
      return {success: true, result: acceptances};
    } catch (error: any) {
      log.error('Error loading policy acceptances:', error);
    }
    return {success: false, message: 'No se pudieron cargar las aceptaciones.'};
  }

  async createPolicy(payload: {
    name: string;
    content?: string;
    html?: string;
    status?: string;
    groupIds?: Array<string | number>;
    employeeIds?: Array<string | number>;
    variableIds?: Array<string | number>;
    controlIds?: Array<string | number>;
    automation?: string;
  }): Promise<ApiResponse<PolicyDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.POLICIES);
      const headers = this.buildHeaders();
      const response: any = await this.apiService.post(
        url,
        this.mapPolicyPayload(payload),
        headers ? {headers} : undefined
      );
      return {success: true, result: this.mapPolicyDetail(response)};
    } catch (error: any) {
      log.error('Error creating policy:', error);
    }
    return {success: false, message: 'No se pudo crear la politica.'};
  }

  async updatePolicy(policyId: string | number, payload: {
    name?: string;
    content?: string;
    html?: string;
    status?: string;
    groupIds?: Array<string | number>;
    employeeIds?: Array<string | number>;
    variableIds?: Array<string | number>;
    controlIds?: Array<string | number>;
    automation?: string;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<PolicyDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.POLICY_DETAIL, {id: policyId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const response: any = await this.apiService.patch(
        url,
        this.mapPolicyPayload(payload),
        {headers, json: false}
      );
      return {success: true, result: this.mapPolicyDetail(response)};
    } catch (error: any) {
      log.error('Error updating policy:', error);
      const message = this.extractErrorMessage(error);
      if (message.includes('cannot be modified') || message.includes('acceptances')) {
        return {success: false, message: 'No se puede modificar una política con aceptaciones registradas.'};
      }
    }
    return {success: false, message: 'No se pudo actualizar la politica.'};
  }

  async publishPolicy(policyId: string | number, payload: {
    groupIds?: Array<string | number>;
    employeeIds?: Array<string | number>;
    variableIds?: Array<string | number>;
    controlIds?: Array<string | number>;
    automation?: string;
  } = {}, options?: {skipLoader?: boolean}): Promise<ApiResponse<PolicyDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.POLICY_PUBLISH, {id: policyId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(
        url,
        this.mapPolicyPayload(payload),
        headers ? {headers} : undefined
      );
      return {success: true, result: this.mapPolicyDetail(response)};
    } catch (error: any) {
      log.error('Error publishing policy:', error);
    }
    return {success: false, message: 'No se pudo enviar la politica.'};
  }

  async unpublishPolicy(policyId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<PolicyDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.POLICY_UNPUBLISH, {id: policyId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, {}, headers ? {headers} : undefined);
      return {success: true, result: this.mapPolicyDetail(response)};
    } catch (error: any) {
      log.error('Error unpublishing policy:', error);
    }
    return {success: false, message: 'No se pudo despublicar la politica.'};
  }

  async deletePolicy(policyId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.POLICY_DETAIL, {id: policyId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting policy:', error);
      const message = this.extractErrorMessage(error);
      if (message.includes('cannot be modified') || message.includes('acceptances')) {
        return {success: false, message: 'No se puede eliminar una política con aceptaciones registradas.'};
      }
    }
    return {success: false, message: 'No se pudo eliminar la política.'};
  }

  async acceptPolicy(policyId: string | number): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.POLICY_ACCEPT, {id: policyId});
      const headers = this.buildHeaders();
      await this.apiService.post(url, {}, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error accepting policy:', error);
    }
    return {success: false, message: 'No se pudo aceptar la politica.'};
  }

  async downloadPolicyPdf(policyId: string | number, type: 'policy' | 'acceptances' = 'policy'): Promise<ApiResponse<Blob>> {
    try {
      const endpoint = type === 'acceptances' ? ENDPOINTS.POLICY_PDF_ACCEPTANCES : ENDPOINTS.POLICY_PDF;
      const url = GET_URL(endpoint, {id: policyId});
      const headers = this.buildHeaders();
      const response: Blob = await this.apiService.get(url, {
        headers: headers || undefined,
        responseType: 'blob',
        skipAuthLogout: true
      });
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error downloading policy pdf:', error);
    }
    return {success: false, message: 'No se pudo descargar el pdf.'};
  }

  private mapPolicy(item: any): PolicyRow {
    const name = item.name || item.title || 'Sin nombre';
    const sinceAtSource = item.sinceAt || item.createdAt || item.publishedAt || item.created_at;
    const sinceAt = sinceAtSource ? new Date(sinceAtSource).toLocaleDateString('es-ES') : '';
    const employeesSource = item.employees ?? item.employeesCount ?? item.targets ?? item.targetsCount ?? item.assignedEmployees?.length ?? 0;
    const employees = Array.isArray(employeesSource) ? employeesSource.length : employeesSource;
    const acceptance = item.acceptance ?? item.acceptanceRate ?? item.acceptedRate ?? 0;
    const status = item.status || item.state || item.lifecycle || 'draft';
    const automation = item.automation?.name || item.automationType || item.automation || item.assignmentStrategy || '';
    const acceptanceCount = item.acceptancesCount ?? item.acceptanceCount ?? item.acceptances?.length;
    const acceptedAt = item.acceptedAt || item.accepted_at || null;
    const canEdit = acceptanceCount ? Number(acceptanceCount) === 0 : !acceptedAt;
    return {
      id: item.id,
      name,
      sinceAt,
      employees,
      acceptance,
      status,
      automation,
      canEdit
    };
  }

  private mapPolicyDetail(item: any): PolicyDetail {
    const base = this.mapPolicy(item);
    const content = item.content || item.html || item.body || item.description || '';
    const attachments = this.mapAttachments(item);
    const assignments = this.mapAssignments(item);
    const influences = this.mapInfluences(item);
    const acceptanceCount = item.acceptancesCount ?? item.acceptanceCount ?? item.acceptances?.length;
    const acceptedAt = item.acceptedAt || item.accepted_at || null;
    const canEdit = acceptanceCount ? Number(acceptanceCount) === 0 : !acceptedAt;
    const readProgress = item.readProgress ?? item.readingProgress ?? item.progress ?? 0;

    return {
      ...base,
      content,
      attachments,
      assignments,
      influences,
      riskVariableIds: this.mapRelatedIds(item.riskVariables),
      controlIds: this.mapRelatedIds(item.controls),
      acceptedAt,
      canEdit,
      readProgress
    };
  }

  private mapPolicyAcceptance(item: any): PolicyAcceptance {
    const employee = item.employee || {};
    const employeeName = item.employeeName
      || employee.name
      || `${employee.firstName || ''} ${employee.lastName || ''}`.trim()
      || 'Empleado';

    return {
      id: item.id,
      employeeName,
      acceptedAt: item.acceptedAt || item.accepted_at || null,
      ipAddress: item.ipAddress || item.ip_address || ''
    };
  }

  private mapAttachments(item: any): Attachment[] {
    const list = item.attachments || item.documents || item.files || [];
    if (!Array.isArray(list)) return [];
    return list.map((entry: any) => ({
      '@context': entry['@context'],
      '@id': entry['@id'],
      '@type': entry['@type'],
      id: entry.id,
      fileName: entry.fileName || entry.name || entry.filename || 'Documento',
      fileSize: entry.fileSize || entry.size || entry.bytes,
      mimeType: entry.mimeType || entry.type,
      filePath: entry.filePath || entry.path,
      createdAt: entry.createdAt || entry.created_at
    }));
  }

  private mapAssignments(item: any): PolicyAssignment[] {
    const assignments: PolicyAssignment[] = [];
    const groups = Array.isArray(item.groups) ? item.groups : (Array.isArray(item.assignedGroups) ? item.assignedGroups : []);
    const employees = Array.isArray(item.employees) ? item.employees : (Array.isArray(item.assignedEmployees) ? item.assignedEmployees : []);
    const assignees = Array.isArray(item.assignments) ? item.assignments : [];

    groups.forEach((group: any) => {
      assignments.push({
        id: group.id,
        name: group.name || group.title || 'Grupo',
        role: group.role || group.function || group.position || 'Grupo',
        type: 'group'
      });
    });

    employees.forEach((employee: any) => {
      assignments.push({
        id: employee.id,
        name: employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Empleado',
        role: employee.role || employee.position || employee.jobTitle || 'Empleado',
        type: 'employee'
      });
    });

    assignees.forEach((assignee: any) => {
      if (!assignee) return;
      assignments.push({
        id: assignee.id,
        name: assignee.name || assignee.title || 'Asignado',
        role: assignee.role || assignee.position || assignee.type || 'Asignado',
        type: assignee.type || 'assignment'
      });
    });

    return assignments;
  }

  private mapPolicyPayload(payload: {
    name?: string;
    content?: string;
    html?: string;
    status?: string;
    groupIds?: Array<string | number>;
    employeeIds?: Array<string | number>;
    variableIds?: Array<string | number>;
    controlIds?: Array<string | number>;
    automation?: string;
  }) {
    const body: any = {...payload};
    if (body.automation) {
      body.automationType = body.automation;
    }
    delete body.automation;

    if (Array.isArray(body.groupIds)) {
      body.assignedGroups = body.groupIds.map((id: string | number) => this.buildIri('groups', id));
    }
    if (Array.isArray(body.employeeIds)) {
      body.assignedEmployees = body.employeeIds.map((id: string | number) => this.buildIri('employees', id));
    }
    if (Array.isArray(body.variableIds)) {
      body.riskVariables = body.variableIds.map((id: string | number) => this.buildIri('risk/variables', id));
    }
    if (Array.isArray(body.controlIds)) {
      body.controls = body.controlIds.map((id: string | number) => this.buildIri('controls', id));
    }
    delete body.groupIds;
    delete body.employeeIds;
    delete body.variableIds;
    delete body.controlIds;

    return body;
  }

  private buildIri(resource: string, id: string | number) {
    const prefix = CONFIG.API_PREFIX.startsWith('/') ? CONFIG.API_PREFIX : `/${CONFIG.API_PREFIX}`;
    const normalized = prefix.endsWith('/') ? prefix : `${prefix}/`;
    return `${normalized}${resource}/${id}`;
  }

  private mapInfluences(item: any): PolicyInfluence[] {
    const list = item.influences || item.variables || item.riskVariables || [];
    if (!Array.isArray(list)) return [];
    return list.map((entry: any) => ({
      variable: entry.variable || entry.name || entry.title || 'Variable',
      behavior: entry.behavior || entry.conduct || entry.control || entry.action || 'Conducta',
      influence: entry.influence || entry.weight || entry.impact || entry.score || 'Influencia'
    }));
  }

  private mapRelatedIds(list: any): Array<string | number> {
    if (!Array.isArray(list)) return [];
    return list
      .map((entry: any) => {
        if (entry?.id !== undefined && entry?.id !== null) return entry.id;
        if (typeof entry === 'string' || typeof entry === 'number') return entry;
        if (typeof entry?.['@id'] === 'string') {
          const parts = entry['@id'].split('/').filter(Boolean);
          return parts[parts.length - 1];
        }
        return null;
      })
      .filter((id: string | number | null): id is string | number => id !== null);
  }

  private extractErrorMessage(error: any): string {
    if (!error) return '';
    if (typeof error.message === 'string') return error.message;
    if (typeof error?.detail === 'string') return error.detail;
    if (typeof error?.description === 'string') return error.description;
    if (typeof error?.details?.detail === 'string') return error.details.detail;
    return '';
  }

  private buildHeaders(skipLoader = false): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }
    return headers;
  }
}
